import Phaser from 'phaser';
import { IBoardTile, BoardEnd } from '../core/types';
import { DominoTile } from './DominoTile';
import { COLORS, TILE } from '../utils/Constants';
import { ParticleEffects } from '../animations/ParticleEffects';

export class DominoBoard extends Phaser.GameObjects.Container {
  private boardTiles: DominoTile[] = [];
  private boardData: IBoardTile[] = [];
  private boardBg: Phaser.GameObjects.Graphics;
  
  private boardWidth: number;
  private boardHeight: number;

  constructor(scene: Phaser.Scene, x: number, y: number, width: number, height: number) {
    super(scene, x, y);
    this.boardWidth = width;
    this.boardHeight = height;
    
    this.boardBg = scene.add.graphics();
    this.add(this.boardBg);
    
    this.drawBackground();
    scene.add.existing(this);
  }

  private drawBackground() {
    this.boardBg.clear();
    const w = this.boardWidth;
    const h = this.boardHeight;
    const r = 20;
    
    const hW = w/2;
    const hH = h/2;
    
    // Outer border (gold)
    this.boardBg.fillStyle(COLORS?.GOLD || 0xd4af37, 1);
    this.boardBg.fillRoundedRect(-hW - 4, -hH - 4, w + 8, h + 8, r + 4);
    
    // Inner felt
    this.boardBg.fillStyle(COLORS.CASINO_FELT, 1);
    this.boardBg.fillRoundedRect(-hW, -hH, w, h, r);
    
    // Inner shadow simulation
    this.boardBg.lineStyle(4, 0x000000, 0.3);
    this.boardBg.strokeRoundedRect(-hW, -hH, w, h, r);
  }

  public addTile(boardTile: IBoardTile, end: BoardEnd, animate: boolean = true) {
    if (end === 'left') {
      this.boardData.unshift(boardTile);
    } else {
      this.boardData.push(boardTile);
    }
    
    const tile = new DominoTile(this.scene, 0, 0, boardTile.tile, true);
    this.add(tile);
    
    if (end === 'left') {
      this.boardTiles.unshift(tile);
    } else {
      this.boardTiles.push(tile);
    }
    
    // Initial position for layout calculation
    this.layoutChain();
    
    if (animate) {
      const targetX = tile.x;
      const targetY = tile.y;
      
      // Start slightly above
      tile.setPosition(targetX, targetY - 50);
      tile.setAlpha(0);
      
      this.scene.tweens.add({
        targets: tile,
        y: targetY,
        alpha: 1,
        duration: 400,
        ease: 'Back.easeOut',
        onComplete: () => {
          ParticleEffects.sparkleAtPoint(this.scene, this.x + targetX, this.y + targetY);
        }
      });
    }
  }

  public layoutChain() {
    if (this.boardData.length === 0) return;

    let currentX = 0;
    const gap = 2;
    const tW = TILE?.WIDTH || 50;
    const tH = TILE?.HEIGHT || 90;
    
    const positions = this.boardData.map((bTile, index) => {
      const t = this.boardTiles[index];
      const isDouble = bTile.tile.a === bTile.tile.b;
      
      t.setHorizontal(!isDouble);
      
      const width = isDouble ? tW : tH;
      
      const pos = { x: currentX + width / 2, y: 0 };
      currentX += width + gap;
      
      return pos;
    });
    
    const totalWidth = currentX - gap;
    const offsetX = -totalWidth / 2;
    
    positions.forEach((pos, index) => {
      const t = this.boardTiles[index];
      const targetX = pos.x + offsetX;
      const targetY = pos.y;
      
      this.scene.tweens.add({
        targets: t,
        x: targetX,
        y: targetY,
        duration: 300,
        ease: 'Power2'
      });
    });
    
    // Auto scale if too wide
    if (totalWidth > this.boardWidth - 40) {
      const scale = (this.boardWidth - 40) / totalWidth;
      this.scene.tweens.add({
        targets: this,
        scaleX: Math.max(scale, 0.4),
        scaleY: Math.max(scale, 0.4),
        duration: 300
      });
    } else {
      this.scene.tweens.add({
        targets: this,
        scaleX: 1,
        scaleY: 1,
        duration: 300
      });
    }
  }

  public clear() {
    this.boardTiles.forEach(t => t.destroy());
    this.boardTiles = [];
    this.boardData = [];
    this.setScale(1);
  }

  public getChainBounds() {
    let minX = 0, maxX = 0;
    const tH = TILE?.HEIGHT || 90;
    this.boardTiles.forEach(t => {
      // rough bounds
      if (t.x - 45 < minX) minX = t.x - 45;
      if (t.x + 45 > maxX) maxX = t.x + 45;
    });
    return { width: maxX - minX, height: tH };
  }
}
